import React, { useState } from 'react';

import { AvatarInput } from '@components/admin/AvatarInput';
import { FormTitle } from '@components/admin/FormTitle';
import { Layout } from '@components/admin/Layout';
import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import { TextInput } from '@components/Forms/TextInput';
import { SpinnerModal } from '@components/SpinnerModal';
import { VerticalSpace } from '@components/VerticalSpace';
import { usePatchUser } from '@hooks/mutation/usePatchUser';
import { useAgencyList } from '@hooks/queries/dashboard/useAgencyList';
import { useUser } from '@hooks/queries/useUser';
import { mapRoleToRoleOptionValue, getRoleOptions } from '@models/Role';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { useQueryClient } from '@tanstack/react-query';
import { FormError } from '@utils/errors';
import { createValidate, requiredStringField } from '@utils/forms';
import { createStrings } from '@utils/local';
import { PageMeta, url } from '@utils/pages';
import clsx from 'clsx';
import { Form, Formik } from 'formik';
import * as t from 'io-ts';
import { useHistory } from 'react-router-dom';

const strings = createStrings({
  ja: {
    title: 'プロフィール',
    employeeId: '社員ID',
    first_name: '名',
    last_name: '姓',
    email: 'メール',
    password: 'パスワード',
    resetPassword: 'リセット',
    update: '更新',
    uploadAvatarHelperText: '画像をアップロード',
    role: '権限グループ',
    updateSuccess: 'プロフィールが更新されました。',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
  },
  backToList: {
    ...common.middleText,
    ...common.fontWeight500,
    color: theme.palette.grey[700],
    marginLeft: 9,
    cursor: 'pointer',
  },
  formBodyContainer: {
    ...common.flexColumn,
    alignItems: 'center',
    paddingTop: '2rem',
    flexGrow: 1,
    rowGap: '20px',
  },
  formBody: {
    ...common.flexColumn,
    alignItems: 'left',
    rowGap: '20px',
  },
  label: {
    ...common.middleText,
    color: theme.palette.grey[800],
  },
  input: {
    ...common.middleText,
  },
  textInput: {
    borderColor: theme.palette.grey[400],
    width: 300,
  },
  rowContainer: {
    ...common.flexCenter,
    width: '100%',
  },
  button: {
    minWidth: 80,
  },
  resetPwdButtonContainer: {
    width: 300,
  },
  resetPwdButton: {
    border: `solid 1px ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
    marginLeft: 10,
  },
}));

const schema = t.type({
  employeeId: requiredStringField(strings.employeeId),
  last_name: requiredStringField(strings.last_name),
  first_name: requiredStringField(strings.first_name),
});

export const Profile: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const queryClient = useQueryClient();
  const { isLoading: isUserLoading, data: userData } = useUser();
  const agencyList = useAgencyList();
  const [shouldShowUpdateSuccess, setShouldUpdateSuccess] = useState(false);

  const history = useHistory();
  const { mutateAsync, isLoading, isError, error, isSuccess, reset } = usePatchUser();

  return (
    <Layout title={strings.title} loading={isUserLoading || agencyList.isLoading}>
      {() => (
        <div className={classes.container}>
          <SpinnerModal open={isLoading} />
          <FormTitle title={strings.title} />
          <Formik
            initialValues={{
              avatar: userData!.profile.avatar_sm,
              employeeId: userData!.staff_id || '',
              first_name: userData!.first_name || '',
              last_name: userData!.last_name || '',
              role: mapRoleToRoleOptionValue(userData?.role, userData?.agency_id ?? 0) || '',
              email: userData!.email || '',
            }}
            validate={createValidate(schema)}
            onSubmit={async (values) => {
              setShouldUpdateSuccess(false);
              const resolvedValues = {
                staff_id: values.employeeId,
                first_name: values.first_name,
                last_name: values.last_name,
              };
              await mutateAsync(resolvedValues);
              setShouldUpdateSuccess(true);
            }}
          >
            <Form className={classes.formBodyContainer}>
              <Alert open={isError} severity="error" onClose={reset}>
                {error instanceof FormError && error.getDisplayMessage()}
              </Alert>
              <Alert
                severity="success"
                open={isSuccess && shouldShowUpdateSuccess}
                onClose={() => {
                  setShouldUpdateSuccess(false);
                }}
              >
                {strings.updateSuccess}
              </Alert>
              <div className={clsx(classes.formBody)}>
                <AvatarInput
                  name="avatar"
                  imageWidth={120}
                  imageHeight={120}
                  round
                  helperText={strings.uploadAvatarHelperText}
                  onChanged={() => {
                    queryClient.invalidateQueries(['/user/']);
                  }}
                />
                <TextInput
                  label={`${strings.employeeId}:`}
                  name="employeeId"
                  type="text"
                  formClasses={{
                    label: classes.label,
                  }}
                  classes={{
                    textInput: clsx(classes.textInput),
                    input: classes.input,
                  }}
                  required
                />
                <TextInput
                  label={`${strings.last_name}:`}
                  name="last_name"
                  type="text"
                  formClasses={{
                    label: classes.label,
                  }}
                  classes={{
                    textInput: clsx(classes.textInput),
                    input: classes.input,
                  }}
                />
                <TextInput
                  label={`${strings.first_name}:`}
                  name="first_name"
                  type="text"
                  formClasses={{
                    label: classes.label,
                  }}
                  classes={{
                    textInput: clsx(classes.textInput),
                    input: classes.input,
                  }}
                />
                <TextInput
                  label={`${strings.role}:`}
                  name="role"
                  type="text"
                  disabled
                  autoComplete="role"
                  formClasses={{
                    label: classes.label,
                  }}
                  classes={{
                    textInput: clsx(classes.textInput),
                    input: classes.input,
                  }}
                  choose
                  options={getRoleOptions(agencyList.data ?? [])}
                />
                <TextInput
                  label={`${strings.email}:`}
                  name="email"
                  type="text"
                  disabled
                  autoComplete="email"
                  formClasses={{
                    label: classes.label,
                  }}
                  classes={{
                    textInput: clsx(classes.textInput),
                    input: classes.input,
                  }}
                />
                <div className={classes.rowContainer}>
                  <span className={clsx(classes.label)}>{`${strings.password}:`}</span>
                  <div className={classes.resetPwdButtonContainer}>
                    <Button
                      text={strings.resetPassword!}
                      color="default"
                      shape="round"
                      className={classes.resetPwdButton}
                      onClick={() => {
                        history.push(url('/admin/staffs/Password'));
                      }}
                    />
                  </div>
                </div>
                <VerticalSpace />
                <div className={classes.rowContainer}>
                  <Button
                    text={strings.update!}
                    color="primary"
                    className={classes.button}
                    shape="round"
                    type="submit"
                  />
                </div>
              </div>
            </Form>
          </Formik>
        </div>
      )}
    </Layout>
  );
};

export const meta: PageMeta = {
  title: strings.title,
  order: 3,
};
